import { useState, useCallback, useRef, useEffect } from 'react';
import { MatchMessage } from './types';

interface UseMatchChatReturn {
  messages: MatchMessage[];
  isTyping: boolean;
  sendMessage: (text: string) => void;
  selectOption: (messageId: string, optionId: string, otherText?: string) => void;
  selectMultipleOptions: (messageId: string, optionIds: string[], otherText?: string) => void;
  submitText: (messageId: string, text: string) => void;
  submitInfoGather: (messageId: string, responses: Record<string, string>) => void;
  dismissInteraction: (messageId: string) => void;
}

export function useMatchChat(): UseMatchChatReturn {
  const [messages, setMessages] = useState<MatchMessage[]>([]);
  const [isTyping] = useState(false);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(clearTimeout);
    };
  }, []);

  const selectOption = useCallback(
    (messageId: string, optionId: string, otherText?: string) => {
      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.id === messageId) {
            return { ...msg, selectedOptionId: optionId, otherText };
          }
          return msg;
        })
      );

      setMessages((prev) => {
        const sourceMsg = prev.find((m) => m.id === messageId);
        const selectedOption = sourceMsg?.options?.find((o) => o.id === optionId);
        const userContent = otherText || selectedOption?.label || optionId;

        return [
          ...prev,
          {
            id: `user-${Date.now()}`,
            role: 'user' as const,
            content: userContent,
            timestamp: new Date(),
          },
        ];
      });
    },
    []
  );

  const selectMultipleOptions = useCallback(
    (messageId: string, optionIds: string[], otherText?: string) => {
      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.id === messageId) {
            return { ...msg, selectedOptionIds: optionIds, selectedOptionId: optionIds[0], otherText };
          }
          return msg;
        })
      );

      setMessages((prev) => {
        const sourceMsg = prev.find((m) => m.id === messageId);
        const labels = optionIds
          .map((id) => sourceMsg?.options?.find((o) => o.id === id)?.label || id);
        const parts = [...labels, ...(otherText ? [otherText] : [])];

        return [
          ...prev,
          {
            id: `user-multi-${Date.now()}`,
            role: 'user' as const,
            content: parts.join(', '),
            timestamp: new Date(),
          },
        ];
      });
    },
    []
  );

  const submitText = useCallback(
    (messageId: string, text: string) => {
      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.id === messageId) {
            return { ...msg, textResponse: text };
          }
          return msg;
        })
      );

      setMessages((prev) => [
        ...prev,
        {
          id: `user-text-${Date.now()}`,
          role: 'user' as const,
          content: text,
          timestamp: new Date(),
        },
      ]);
    },
    []
  );

  const submitInfoGather = useCallback(
    (messageId: string, responses: Record<string, string>) => {
      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.id === messageId) {
            return { ...msg, infoResponses: responses };
          }
          return msg;
        })
      );

      const sourceMsg = messages.find((m) => m.id === messageId);
      const summaryParts: string[] = [];
      for (const field of sourceMsg?.infoFields || []) {
        const value = responses[field.id];
        if (value) {
          if (field.type === 'choice') {
            const option = field.options?.find((o) => o.id === value);
            summaryParts.push(`${field.label}: ${option?.label || value}`);
          } else {
            summaryParts.push(`${field.label}: ${value}`);
          }
        }
      }

      setMessages((prev) => [
        ...prev,
        {
          id: `user-info-${Date.now()}`,
          role: 'user' as const,
          content: summaryParts.join('\n'),
          timestamp: new Date(),
        },
      ]);
    },
    [messages]
  );

  const dismissInteraction = useCallback(
    (messageId: string) => {
      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.id === messageId) {
            if (msg.options && msg.options.length > 0) {
              return { ...msg, selectedOptionId: '_dismissed' };
            }
            if (msg.textPrompt) {
              return { ...msg, textResponse: '_dismissed' };
            }
            if (msg.infoFields && msg.infoFields.length > 0) {
              return { ...msg, infoResponses: {} };
            }
            return msg;
          }
          return msg;
        })
      );
    },
    []
  );

  const sendMessage = useCallback(
    (text: string) => {
      setMessages((prev) => [
        ...prev,
        {
          id: `user-freeform-${Date.now()}`,
          role: 'user',
          content: text,
          timestamp: new Date(),
        },
      ]);
    },
    []
  );

  return { messages, isTyping, sendMessage, selectOption, selectMultipleOptions, submitText, submitInfoGather, dismissInteraction };
}
